import React from 'react';
import { FormHelpers } from 'ontraccr-common';
import SearchField from './SearchField';
import { operands } from './search.constants';
import { dateOperatorSet } from '../forms/FormBuilder/FormFields/ConditionalRenderingBuilder';

const supportedTypes = {
  text: true,
  attribute: true,
  calculation: true,
  dropdown: true,
  'yes-no': true,
  table: true,
};

export const isSupported = (selectedType, { dataType } = {}) => (
  supportedTypes[selectedType]
  && !(selectedType === 'dropdown' && dataType === 'Sub-Contracts')
);

export const fieldToSearch = (queries, onQueriesChange, isFieldQueries) => function (field) {
  const { id, configProps, selectedType } = field;

  let fieldQueries = queries;
  if (!isFieldQueries) {
    ({ [id]: fieldQueries = [] } = queries || {});
  }
  if (!isSupported(selectedType, configProps)) return null;
  return (
    <SearchField
      configProps={configProps}
      selectedType={selectedType}
      queries={fieldQueries}
      onQueriesChange={onQueriesChange(id)}
      multi
      buttonStyle={{
        backgroundColor: 'transparent',
      }}
    />
  );
};

const validateAccumulatedValue = (value, isInt, formulas) => {
  // Check if the index is valid
  if (isInt && parseInt(value, 10) && parseInt(value, 10) >= formulas.length + 1) {
    return { isValid: false, error: `Invalid index: ${value}` };
  }

  // Check if the term is valid
  if (!isInt && !operands.has(value.trim().toUpperCase())) {
    return { isValid: false, error: `Invalid term found: ${value}` };
  }

  return { isValid: true, error: null };
};


// Assuming the formula is valid, we need to ensure:
// 1. It does not end in operand
// 2. It does not have multiple different operand in same level
// 3. It does not have consecutive operands
// 4. It does not have consecutive indices
// 5. It does not have empty brackets
const validateFormula = (formulaValues, startIndex = 0) => {
  let seenOperand;
  let lastSeenValue;

  for (let i = startIndex; i < formulaValues.length; i += 1) {
    const value = formulaValues[i];
    if (value === '(') {
      const { end, isValid, error } = validateFormula(formulaValues, i + 1);
      if (!isValid) {
        return { isValid, error };
      }
      lastSeenValue = null;
      i = end;
    } else if (operands.has(value?.toUpperCase())) {
      if (lastSeenValue && operands.has(lastSeenValue)) {
        return { isValid: false, error: 'Consecutive operands detected.' };
      }

      if (seenOperand && seenOperand?.toUpperCase() !== value.toUpperCase()) {
        return { isValid: false, error: 'Multiple different operands in same level.' };
      }
      seenOperand = value;
      lastSeenValue = value.toUpperCase();
    } else if (value === ')') {
      if (!lastSeenValue) {
        return { isValid: false, error: 'Empty formula detected.' };
      }

      if (operands.has(lastSeenValue)) {
        return { isValid: false, error: `Formula cannot end with ${lastSeenValue}.` };
      }
      return { isValid: true, start: startIndex, end: i };
    } else {
      if (!operands.has(lastSeenValue)) {
        return { isValid: false, error: 'Formula must have an operand between indices.' };
      }
      lastSeenValue = value;
    }
  }

  if (operands.has(lastSeenValue)) {
    return { isValid: false, error: `Formula cannot end with ${lastSeenValue}.` };
  }

  return { isValid: true, error: null, formulaValues };
};

export const validateNewValue = (newValue, formulas = []) => {
  if (!newValue) return { isValid: false, error: 'Empty formula.' };
  let acc = '';
  const trimmedValue = newValue.trim();
  let isInt = !!parseInt(trimmedValue.charAt(0), 10);
  const bracketStack = [];
  const formulaValues = [];
  for (let i = 0; i < trimmedValue.length; i += 1) {
    const char = trimmedValue[i];
    let hasBracket = false;
    // Check matching brackets
    if (char === '(') {
      bracketStack.push(char);
      hasBracket = true;
      if (acc) formulaValues.push(acc.trim());
      formulaValues.push(char);
      acc = '';
    } else if (char === ')') {
      if (bracketStack.length === 0) {
        return { isValid: false, error: 'Unmatched closing bracket.' };
      }
      bracketStack.pop();
      hasBracket = true;
      if (acc) formulaValues.push(acc.trim());
      formulaValues.push(char);
    }

    if (
      // Check if the character is a number and the previous character was a number
      // or the character is not a number and the previous character was not a number
      // and the current character is not a bracket (as we need to process the accumulated value)
      (
        (parseInt(char, 10) && isInt)
        || (!parseInt(char, 10) && !isInt)
      )
      && !hasBracket
    ) {
      acc += char;
    } else {
      const { isValid, error } = validateAccumulatedValue(acc, isInt, formulas);
      if (!isValid && acc) {
        return { isValid, error };
      }
      if (acc && !hasBracket) formulaValues.push(acc.trim());
      acc = hasBracket ? '' : char;
      isInt = hasBracket ? !!parseInt(trimmedValue.at(i + 1), 10) : !!parseInt(char, 10);
    }
  }

  if (bracketStack.length > 0) {
    return { isValid: false, error: 'Unmatched opening bracket.' };
  }

  if (acc) {
    const { isValid, error } = validateAccumulatedValue(acc, isInt, formulas);
    formulaValues.push(acc.trim());
    if (!isValid) {
      return { isValid, error };
    }
  }

  return validateFormula(formulaValues);
};

export const getMostRecentDateFields = (formulas = []) => (
  formulas
    .filter((f) => f?.field?.endsWith?.('.mostRecentDate'))
    .map((f) => f.field.replace('.mostRecentDate', ''))
);

// TODO: Add logic for converting custom filter rule
export const convertQueryToOpenSearchQuery = (query, filterType) => {
  const operand = filterType === 'and' ? 'AND' : 'OR';

  const openSearchQuery = {
    [operand]: [],
  };

  query?.formulas?.forEach((formula) => {
    const baseFormula = {
      ...formula,
      field: formula?.field?.startsWith?.('field-') ? `customFields.${formula.field}` : formula.field,
    };

    if (!FormHelpers.isIndividualConditionalRenderingFormulaComplete(formula)) {
      return;
    }

    // Adds logic to support date range queries
    if (dateOperatorSet.has(formula.operator)) {
      openSearchQuery[operand].push({
        OR: [
          baseFormula, // Date Time Query
          {
            // Date Range Query
            AND: [
              {
                ...baseFormula,
                field: `${baseFormula.field}-startTime`,
              },
              {
                ...baseFormula,
                field: `${baseFormula.field}-endTime`,
              },
            ],
          },
        ],
      });
    } else if (formula.operator?.includes('dropdown')) {
      const [operator] = formula.operator.split('-');
      // has/!has is associated with names as it is a string comparison
      // =/!= is associated with ids as it is a value comparison
      const suffix = (operator === 'has' || operator === '!has') ? 'names' : 'ids';
      let { value } = baseFormula;
      // Color legend Select values are `${label}::${hex}` so equivalent labels across
      // boards can share one option; color-ids is indexed by hex only.
      if (baseFormula.field === 'color' && suffix === 'ids' && Array.isArray(value)) {
        value = value.map((color) => (
          typeof color === 'string' && color.includes('::')
            ? color.slice(color.lastIndexOf('::') + 2)
            : color
        ));
      }
      openSearchQuery[operand].push({
        ...baseFormula,
        operator,
        value,
        field: `${baseFormula.field}-${suffix}`,
      });
    } else {
      openSearchQuery[operand].push(baseFormula);
    }
  });

  return openSearchQuery;
};

export const getSearchDrawerState = (type, currentQuery, selectedQuery) => {
  const selectedIsSameType = selectedQuery?.type === type;
  if (currentQuery?.type === type && currentQuery?.query) {
    return {
      ourQuery: currentQuery.query,
      filterType: currentQuery.filterType ?? selectedQuery?.filterType ?? 'and',
      selectedQuery: selectedIsSameType ? selectedQuery : undefined,
      customFilterRule: selectedIsSameType
        ? (selectedQuery.customFilterRule ?? null)
        : null,
    };
  }

  if (selectedIsSameType) {
    return {
      ourQuery: selectedQuery.query ?? {},
      filterType: selectedQuery.filterType || 'and',
      selectedQuery,
      customFilterRule: selectedQuery.customFilterRule || null,
    };
  }

  return null;
};
